/** HTTP + WebSocket client for the Samra server (same origin). */
import type { ServerMsg } from "@samra/shared";

export class ApiError extends Error {
  constructor(public status: number, public code: string, public extra: any) {
    super(code);
  }
}

/** Default per-request budget. A request that exceeds it is reported as `request_timeout`, never as a silent hang. */
export const DEFAULT_TIMEOUT_MS = 20_000;

export interface ApiOptions {
  timeoutMs?: number;
}

export async function api<T = any>(method: string, path: string, body?: unknown, opts: ApiOptions = {}): Promise<T> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), opts.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  try {
    let res: Response;
    try {
      res = await fetch(path, {
        method,
        credentials: "same-origin",
        headers: { "content-type": "application/json", "x-samra-csrf": "1" },
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: ctrl.signal,
      });
    } catch {
      // Aborted by our timer, or the network failed before a response arrived.
      throw new ApiError(0, ctrl.signal.aborted ? "request_timeout" : "network_error", {});
    }
    let text: string;
    try {
      text = await res.text();
    } catch {
      throw new ApiError(0, ctrl.signal.aborted ? "request_timeout" : "network_error", {});
    }
    let json: any = {};
    if (text) {
      try {
        json = JSON.parse(text);
      } catch {
        // A proxy error page or truncated body: never pretend it is a valid answer.
        throw new ApiError(res.status || 0, res.ok ? "bad_response" : "error", {});
      }
    }
    if (!res.ok) throw new ApiError(res.status, json.error ?? "error", json);
    return json as T;
  } finally {
    clearTimeout(timer);
  }
}
export const get = <T = any>(p: string, opts?: ApiOptions) => api<T>("GET", p, undefined, opts);
export const post = <T = any>(p: string, b: unknown = {}, opts?: ApiOptions) => api<T>("POST", p, b, opts);
export const patch = <T = any>(p: string, b: unknown = {}, opts?: ApiOptions) => api<T>("PATCH", p, b, opts);
export const put = <T = any>(p: string, b: unknown = {}, opts?: ApiOptions) => api<T>("PUT", p, b, opts);
export const del = <T = any>(p: string, b: unknown = {}, opts?: ApiOptions) => api<T>("DELETE", p, b, opts);

/** True when the outcome of a request is unknown (timeout or no connection), so the caller must check state, not assume failure. */
export const outcomeUnknown = (e: unknown) => e instanceof ApiError && (e.code === "request_timeout" || e.code === "network_error");

export const newKey = () =>
  (crypto.randomUUID?.() ?? Math.random().toString(36).slice(2) + Date.now().toString(36)).replace(/[^A-Za-z0-9_-]/g, "");

/* ───────── realtime ───────── */
type Listener = (m: ServerMsg) => void;
const listeners = new Set<Listener>();
let ws: WebSocket | null = null;
let wanted = false;
let retry = 0;
const pending: string[] = [];
const joinedRooms = new Set<string>();
let statusCb: (s: "online" | "connecting" | "offline") => void = () => {};

export function onStatus(cb: typeof statusCb) {
  statusCb = cb;
}

export function connect() {
  wanted = true;
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
  statusCb("connecting");
  const proto = location.protocol === "https:" ? "wss" : "ws";
  ws = new WebSocket(`${proto}://${location.host}/ws`); // session cookie authenticates the socket
  ws.onopen = () => {
    retry = 0;
  };
  ws.onmessage = (e) => {
    const m = JSON.parse(e.data) as ServerMsg;
    if (m.t === "welcome") {
      statusCb("online");
      for (const r of joinedRooms) ws!.send(JSON.stringify({ t: "room.join", roomId: r }));
      while (pending.length) ws!.send(pending.shift()!);
    }
    for (const l of listeners) l(m);
  };
  ws.onclose = () => {
    statusCb("offline");
    ws = null;
    if (!wanted) return;
    const delay = Math.min(10_000, 500 * 2 ** retry++);
    setTimeout(connect, delay);
  };
}

export function disconnect() {
  wanted = false;
  ws?.close();
  ws = null;
}

export function send(msg: object) {
  const s = JSON.stringify(msg);
  if (ws && ws.readyState === WebSocket.OPEN) ws.send(s);
  else {
    pending.push(s);
    connect();
  }
}

export function joinRoomChannel(roomId: string) {
  joinedRooms.add(roomId);
  send({ t: "room.join", roomId });
}
export function leaveRoomChannel(roomId: string) {
  joinedRooms.delete(roomId);
  send({ t: "room.leave", roomId });
}

export function subscribe(l: Listener) {
  listeners.add(l);
  // Returns void so it can be returned straight from a React effect as its cleanup.
  return () => {
    listeners.delete(l);
  };
}

// keep-alive for proxies that drop idle sockets
setInterval(() => ws?.readyState === WebSocket.OPEN && ws.send(JSON.stringify({ t: "ping" })), 20_000);
